import { useMemo, useState, type ChangeEvent } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "wouter";
import Papa from "papaparse";
import { Upload, FileSpreadsheet, ChevronRight } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { useAlmacen } from "@/lib/almacen";
import { useAuth } from "@/auth/AuthProvider";
import { useRpc } from "@/lib/useRpc";
import { num, fechaHora } from "@/lib/formato";
import { Tarjeta, Boton, Campo, Select, Badge, Esqueleto, Vacio } from "@/components/ui";
import { useToast } from "@/components/ui/Toast";
import { detectarCabecera, mapeoAutomatico, parsearHoja, decodificarCsv, detectarSeparador, type Mapeo, type Columna, type ResultadoParseo } from "./parsear";

type Hoja = { nombre: string; celdas: unknown[][] };
type Paso = 1 | 2 | 3 | 4 | 5;
const COLUMNAS: { col: Columna; etiqueta: string; requerida?: boolean }[] = [
  { col: "sku", etiqueta: "Clave (SKU)", requerida: true },
  { col: "existencia", etiqueta: "Existencia", requerida: true },
  { col: "nombre", etiqueta: "Nombre / descripción" },
  { col: "unidad", etiqueta: "Unidad" },
  { col: "minimo", etiqueta: "Mínimo" },
  { col: "ubicacion", etiqueta: "Ubicación (A-01-03)" },
  { col: "consumo", etiqueta: "Rotación esperada" },
];
const LOTE = 2000;

export function Importar() {
  const { almacen, almacenes } = useAlmacen();
  const { esAdmin } = useAuth();
  const toast = useToast();
  const [paso, setPaso] = useState<Paso>(1);
  const [archivo, setArchivo] = useState<string>("");
  const [hojas, setHojas] = useState<Hoja[]>([]);
  const [hojaIdx, setHojaIdx] = useState(0);
  const [almacenId, setAlmacenId] = useState<string>("");
  const [filaCab, setFilaCab] = useState(0);
  const [mapeo, setMapeo] = useState<Mapeo>({});
  const [periodo, setPeriodo] = useState<30 | 90>(30);
  const [actualizarUbic, setActualizarUbic] = useState(false);
  const [desactivar, setDesactivar] = useState(false);
  const [leyendo, setLeyendo] = useState(false);
  const [resultado, setResultado] = useState<{ ok: number; rechazadas: number; detalle: { fila: number; sku: string | null; motivo: string }[]; total: number } | null>(null);
  const [progreso, setProgreso] = useState<string | null>(null);

  const historial = useQuery({
    queryKey: ["importaciones"],
    queryFn: async () => { const { data, error } = await supabase.from("importaciones").select("*, almacenes(nombre)").order("fecha", { ascending: false }).limit(10); if (error) throw error; return data; },
  });
  const importar = useRpc("importar_catalogo", { silencioso: true });

  const hoja = hojas[hojaIdx];
  const cabeceras = hoja?.celdas[filaCab] ?? [];
  const parseo: ResultadoParseo | null = useMemo(() => (hoja && mapeo.sku !== undefined && mapeo.existencia !== undefined ? parsearHoja(hoja.celdas, mapeo, filaCab, { periodo }) : null), [hoja, mapeo, filaCab, periodo]);

  async function leerArchivo(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0];
    if (!f) return;
    setLeyendo(true);
    setArchivo(f.name);
    try {
      const buf = await f.arrayBuffer();
      let nuevas: Hoja[];
      if (/\.xlsx?$|\.xlsm$|\.ods$/i.test(f.name)) {
        const XLSX = await import("xlsx");
        const wb = XLSX.read(buf, { type: "array", cellDates: false, raw: false });
        // raw:true → las celdas numéricas llegan como number (sin el formato "1,250" que engaña al parser)
        nuevas = wb.SheetNames.map((n) => ({ nombre: n, celdas: XLSX.utils.sheet_to_json<unknown[]>(wb.Sheets[n], { header: 1, raw: true, defval: "", blankrows: true }) }));
      } else {
        const { texto } = decodificarCsv(buf);
        const sep = detectarSeparador(texto);
        const parsed = Papa.parse<string[]>(texto, { delimiter: sep, skipEmptyLines: false });
        nuevas = [{ nombre: "CSV", celdas: parsed.data }];
      }
      nuevas = nuevas.filter((h) => h.celdas.some((r) => r.some((c) => String(c ?? "").trim() !== "")));
      if (nuevas.length === 0) { toast.error("El archivo está vacío o no lo pude leer. Exporta como CSV UTF-8 o .xlsx."); setLeyendo(false); return; }
      setHojas(nuevas);
      elegirHoja(nuevas, 0);
      setAlmacenId(almacen?.id ?? "");
      setPaso(2);
    } catch (err) {
      console.error(err);
      toast.error("No pude leer el archivo. Exporta como CSV UTF-8 o .xlsx e intenta de nuevo.");
    } finally {
      setLeyendo(false);
      e.target.value = "";
    }
  }

  function elegirHoja(lista: Hoja[], idx: number) {
    setHojaIdx(idx);
    const cab = detectarCabecera(lista[idx].celdas);
    setFilaCab(cab);
    const m = mapeoAutomatico(lista[idx].celdas[cab] ?? []);
    setMapeo(m);
    if (m.consumo !== undefined) {
      const c = String(lista[idx].celdas[cab]?.[m.consumo] ?? "").toLowerCase();
      setPeriodo(/3 meses|trimestr|90/.test(c) ? 90 : 30);
    }
  }

  async function confirmar() {
    if (!parseo || !almacenId) return;
    if (parseo.avisos.some((a) => a.includes("3 dígitos")) && !window.confirm("Hay valores con coma y tres dígitos que se leyeron como decimales (ej. 1,250 → 1.25). ¿Importar de todas formas?")) return;
    // Dedupe entre lotes: la base solo ve duplicados dentro de su llamada; aquí gana la primera fila del archivo.
    const vistos = new Set<string>();
    const filas: typeof parseo.filas = [];
    const detalle = [...parseo.rechazadas];
    for (const f of parseo.filas) {
      const k = f.sku.trim().replace(/\s+/g, " ").toUpperCase();
      if (vistos.has(k)) { detalle.push({ fila: f.fila, sku: f.sku, motivo: "DUPLICADO" }); continue; }
      vistos.add(k); filas.push(f);
    }
    const lotes: typeof filas[] = [];
    for (let i = 0; i < filas.length; i += LOTE) lotes.push(filas.slice(i, i + LOTE));
    const lote = crypto.randomUUID();
    let ok = 0, rechazadas = detalle.length;
    const total = parseo.filas.length + parseo.rechazadas.length;
    for (let i = 0; i < lotes.length; i++) {
      setProgreso(lotes.length > 1 ? `Cargando parte ${i + 1} de ${lotes.length}…` : "Cargando…");
      try {
        const r = await importar.mutateAsync({
          p_almacen_id: almacenId, p_nombre_archivo: `${archivo}${hojas.length > 1 ? ` · ${hoja.nombre}` : ""}${lotes.length > 1 ? ` (${i + 1}/${lotes.length})` : ""}`,
          p_filas: lotes[i] as never, p_actualizar_ubicaciones: actualizarUbic, p_desactivar_ausentes: desactivar && i === lotes.length - 1, p_lote: lote,
        });
        ok += r.filas_ok; rechazadas += r.filas_rechazadas;
        detalle.push(...((r.detalle as never as { fila: number; sku: string | null; motivo: string }[]) ?? []));
      } catch (e) {
        setProgreso(null);
        toast.error((e as { mensaje?: string }).mensaje ?? "Falló la carga");
        return;
      }
    }
    setProgreso(null);
    setResultado({ ok, rechazadas, detalle, total });
    setPaso(5);
  }

  function reiniciar() { setPaso(1); setHojas([]); setResultado(null); setArchivo(""); }

  if (!esAdmin) return <div className="mx-auto max-w-2xl"><Tarjeta><p>Solo un administrador puede importar el catálogo.</p></Tarjeta></div>;
  if (!almacen) return <Esqueleto />;

  return (
    <div className="mx-auto max-w-4xl space-y-4">
      <div><h1 className="text-2xl font-bold">Importar catálogo</h1><p className="text-sm text-tinta-suave">Sube el Excel o CSV de tu inventario. Lo que ya cuentas aquí no se toca: solo se actualiza lo que dice tu archivo.</p></div>

      <ol className="flex flex-wrap items-center gap-1 text-sm text-tinta-suave">
        {["Archivo", "Hoja y almacén", "Columnas", "Vista previa", "Resultado"].map((t, i) => (
          <li key={t} className="flex items-center gap-1"><span className={`rounded-full px-2.5 py-0.5 ${paso === i + 1 ? "bg-sidebar text-white" : paso > i + 1 ? "bg-emerald-100 text-emerald-800" : "bg-gray-100"}`}>{i + 1}. {t}</span>{i < 4 && <ChevronRight className="h-4 w-4" />}</li>
        ))}
      </ol>

      {paso === 1 && (
        <Tarjeta>
          <label className="flex cursor-pointer flex-col items-center gap-3 rounded-md border-2 border-dashed border-borde px-6 py-12 text-center hover:border-primary">
            <Upload className="h-10 w-10 text-tinta-suave" />
            <span className="text-lg font-semibold">{leyendo ? "Leyendo…" : "Elige tu archivo"}</span>
            <span className="text-sm text-tinta-suave">Excel (.xlsx) o CSV. Con columnas como Clave, Descripción, Existencia, Mínimo, Ubicación, Rotación.</span>
            <input type="file" accept=".csv,.txt,.xlsx,.xls,.xlsm,.ods,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" className="hidden" onChange={leerArchivo} disabled={leyendo} />
          </label>
        </Tarjeta>
      )}

      {paso === 2 && hoja && (
        <Tarjeta titulo={<><FileSpreadsheet className="mr-1 inline h-4 w-4" />{archivo}</> as never}>
          <div className="grid gap-4 sm:grid-cols-2">
            {hojas.length > 1 && (
              <Campo etiqueta="Hoja del archivo" htmlFor="hoja" ayuda="Si cada hoja es una bodega, importa una hoja a la vez.">
                <Select id="hoja" value={hojaIdx} onChange={(e) => elegirHoja(hojas, Number(e.target.value))}>{hojas.map((h, i) => <option key={h.nombre} value={i}>{h.nombre} ({h.celdas.length} filas)</option>)}</Select>
              </Campo>
            )}
            <Campo etiqueta="Almacén destino" htmlFor="alm">
              <Select id="alm" value={almacenId} onChange={(e) => setAlmacenId(e.target.value)}>{almacenes.map((a) => <option key={a.id} value={a.id}>{a.nombre}</option>)}</Select>
            </Campo>
            <Campo etiqueta="Fila de cabecera" htmlFor="cab" ayuda="La fila donde están los títulos de las columnas.">
              <Select id="cab" value={filaCab} onChange={(e) => { const c = Number(e.target.value); setFilaCab(c); setMapeo(mapeoAutomatico(hoja.celdas[c] ?? [])); }}>
                {hoja.celdas.slice(0, 15).map((f, i) => <option key={i} value={i}>Fila {i + 1}: {f.slice(0, 5).map((c) => String(c ?? "")).filter(Boolean).join(" · ").slice(0, 60) || "(vacía)"}</option>)}
              </Select>
            </Campo>
          </div>
          <div className="mt-4 flex gap-2"><Boton onClick={() => setPaso(3)} disabled={!almacenId}>Siguiente</Boton><Boton variante="secundario" onClick={reiniciar}>Otro archivo</Boton></div>
        </Tarjeta>
      )}

      {paso === 3 && hoja && (
        <Tarjeta titulo="¿Qué columna es qué?">
          <p className="mb-3 text-sm text-tinta-suave">Detecté las columnas por su nombre; revisa que estén bien. Clave y Existencia son obligatorias.</p>
          <div className="grid gap-3 sm:grid-cols-2">
            {COLUMNAS.map(({ col, etiqueta, requerida }) => (
              <Campo key={col} etiqueta={`${etiqueta}${requerida ? " *" : ""}`} htmlFor={`col-${col}`}>
                <Select id={`col-${col}`} value={mapeo[col] ?? ""} onChange={(e) => setMapeo((m) => ({ ...m, [col]: e.target.value === "" ? undefined : Number(e.target.value) }))}>
                  <option value="">— no viene —</option>
                  {cabeceras.map((c, i) => <option key={i} value={i}>{String(c ?? "").trim() || `(columna ${i + 1})`}</option>)}
                </Select>
              </Campo>
            ))}
            {mapeo.consumo !== undefined && (
              <Campo etiqueta="La rotación es por" htmlFor="periodo"><Select id="periodo" value={periodo} onChange={(e) => setPeriodo(Number(e.target.value) as 30 | 90)}><option value={30}>30 días (mensual)</option><option value={90}>90 días (trimestral)</option></Select></Campo>
            )}
          </div>
          <div className="mt-3 overflow-x-auto rounded border border-borde">
            <table className="w-full text-xs"><tbody>
              {hoja.celdas.slice(filaCab, filaCab + 4).map((f, i) => <tr key={i} className={i === 0 ? "bg-gray-50 font-semibold" : ""}>{f.slice(0, 8).map((c, j) => <td key={j} className="border-b border-borde px-2 py-1">{String(c ?? "")}</td>)}</tr>)}
            </tbody></table>
          </div>
          <div className="mt-4 flex gap-2"><Boton onClick={() => setPaso(4)} disabled={mapeo.sku === undefined || mapeo.existencia === undefined}>Ver vista previa</Boton><Boton variante="secundario" onClick={() => setPaso(2)}>Atrás</Boton></div>
        </Tarjeta>
      )}

      {paso === 4 && parseo && (
        <Tarjeta titulo="Vista previa">
          <div className="flex flex-wrap gap-2">
            <Badge tono="ok">{parseo.filas.length} filas listas</Badge>
            {parseo.rechazadas.length > 0 && <Badge tono="error">{parseo.rechazadas.length} rechazadas</Badge>}
            {parseo.avisos.map((a) => <Badge key={a} tono="alerta">{a}</Badge>)}
          </div>
          {parseo.rechazadas.length > 0 && (
            <ul className="mt-3 max-h-32 overflow-auto rounded border border-borde text-sm">
              {parseo.rechazadas.map((r) => <li key={r.fila} className="flex justify-between border-b border-borde px-3 py-1"><span>Fila {r.fila} {r.sku ?? ""}</span><span className="text-tinta-suave">{etiquetaRechazo(r.motivo)}</span></li>)}
            </ul>
          )}
          <div className="mt-3 overflow-x-auto rounded border border-borde">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-left text-xs uppercase text-tinta-suave"><tr><th className="px-2 py-1">Clave</th><th className="px-2 py-1">Nombre</th><th className="px-2 py-1 text-right">Existencia</th><th className="px-2 py-1 text-right">Mínimo</th><th className="px-2 py-1">Ubicación</th><th className="px-2 py-1 text-right">Rotación</th></tr></thead>
              <tbody>{parseo.filas.slice(0, 8).map((f) => <tr key={f.fila} className="border-t border-borde"><td className="px-2 py-1 font-medium">{f.sku}</td><td className="px-2 py-1">{f.nombre}</td><td className="tabular px-2 py-1 text-right">{num(f.existencia)}</td><td className="tabular px-2 py-1 text-right">{num(f.minimo)}</td><td className="px-2 py-1">{f.ubicacion ?? <span className="text-tinta-suave">sin zona</span>}</td><td className="tabular px-2 py-1 text-right">{f.consumo != null ? `${num(f.consumo)} / ${f.periodo} d` : "—"}</td></tr>)}</tbody>
            </table>
            {parseo.filas.length > 8 && <p className="px-2 py-1 text-xs text-tinta-suave">… y {parseo.filas.length - 8} más</p>}
          </div>
          <div className="mt-4 space-y-2 text-sm">
            <label className="flex items-center gap-2"><input type="checkbox" checked={actualizarUbic} onChange={(e) => setActualizarUbic(e.target.checked)} /> Actualizar la ubicación y zona de productos que ya existen</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={desactivar} onChange={(e) => setDesactivar(e.target.checked)} /> Desactivar los productos de la empresa que no vienen en este archivo</label>
            <p className="text-tinta-suave">Los físicos que ya contaste no se tocan. Los productos nuevos nacen con el físico igual a la existencia del archivo.</p>
          </div>
          <div className="mt-4 flex gap-2"><Boton onClick={confirmar} cargando={!!progreso} disabled={parseo.filas.length === 0}>{progreso ?? `Importar ${parseo.filas.length} productos`}</Boton><Boton variante="secundario" onClick={() => setPaso(3)} disabled={!!progreso}>Atrás</Boton></div>
        </Tarjeta>
      )}

      {paso === 5 && resultado && (
        <Tarjeta titulo="Resultado">
          <p className="text-lg"><strong>{resultado.ok}</strong> productos cargados{resultado.rechazadas > 0 && <>, <strong className="text-red-700">{resultado.rechazadas}</strong> rechazados</>} de {resultado.total}.</p>
          {resultado.detalle.length > 0 && (
            <ul className="mt-3 max-h-48 overflow-auto rounded border border-borde text-sm">
              {resultado.detalle.map((r, i) => <li key={i} className="flex justify-between border-b border-borde px-3 py-1"><span>Fila {r.fila} {r.sku ?? ""}</span><span className="text-tinta-suave">{etiquetaRechazo(r.motivo)}</span></li>)}
            </ul>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Link href="/contar"><Boton>Contar zona</Boton></Link>
            <Link href="/inventario"><Boton variante="secundario">Ver inventario</Boton></Link>
            <Boton variante="secundario" onClick={reiniciar}>Importar otro archivo</Boton>
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Importaciones anteriores">
        {historial.isLoading ? <Esqueleto filas={2} /> : (historial.data?.length ?? 0) === 0 ? <Vacio titulo="Ninguna todavía" /> : (
          <ul className="divide-y divide-borde text-sm">
            {historial.data!.map((i) => (
              <li key={i.id} className="flex flex-wrap items-center justify-between gap-2 py-2">
                <span><span className="font-medium">{i.nombre_archivo}</span> <span className="text-tinta-suave">· {(i as { almacenes?: { nombre: string } | null }).almacenes?.nombre} · {fechaHora(i.fecha)}</span></span>
                <span className="flex items-center gap-2"><Badge tono={i.estado === "ok" ? "ok" : "alerta"}>{i.filas_ok} ok{i.filas_rechazadas ? ` · ${i.filas_rechazadas} rechazadas` : ""}</Badge></span>
              </li>
            ))}
          </ul>
        )}
      </Tarjeta>
    </div>
  );
}

function etiquetaRechazo(m: string): string {
  switch (m) {
    case "SIN_CLAVE": return "sin clave";
    case "DUPLICADO": return "clave repetida (se tomó la primera)";
    case "EXISTENCIA_NO_NUMERICA": return "existencia no es un número";
    case "MINIMO_NO_NUMERICO": return "mínimo no es un número";
    case "MINIMO_NEGATIVO": return "mínimo negativo";
    case "PERIODO_INVALIDO": return "periodo inválido";
    default: return m;
  }
}
